import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Input } from './ui/input';
import { RefreshCw, Download, Search, FileText, Database } from 'lucide-react';
import { toast } from 'sonner';

export default function DataDisplay({ data, onReset }) {
  const [searchTerm, setSearchTerm] = useState('');

  const handleDownload = () => {
    // Create CSV content for documents with results
    const headers = ['Document Name', ...Object.keys(data[0].results || {})];
    const csvContent = [
      headers.join(','),
      ...data.map(item => [
        `"${item.fileName}"`,
        ...Object.values(item.results || {}).map(val => `"${val}"`)
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `extracted_data_${Date.now()}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    
    toast.success('Data exported successfully!');
  };

  // Render for documents with key-value results
  return (
    <div className="space-y-6">
        {/* Success Header */}
        <Card className="shadow-lg border-accent/30 bg-gradient-to-br from-accent/5 to-transparent">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="flex h-14 w-14 items-center justify-center rounded-xl gradient-accent shadow-accent">
                  <Database className="h-7 w-7 text-accent-foreground" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-foreground">Extraction Complete!</h2>
                  <p className="text-muted-foreground mt-1">
                    Successfully extracted data from {data.length} document(s)
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={handleDownload}
                  variant="outline"
                  className="gap-2"
                >
                  <Download className="h-4 w-4" />
                  Export CSV
                </Button>
                <Button
                  onClick={onReset}
                  className="gap-2 gradient-primary hover:opacity-90 transition-smooth"
                >
                  <RefreshCw className="h-4 w-4" />
                  New Extraction
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Document Results */}
        {data.map((doc, docIndex) => (
          <Card key={docIndex} className="shadow-lg border-border/50">
            <CardHeader>
              <div className="flex items-center gap-3">
                <FileText className="h-5 w-5 text-primary" />
                <div>
                  <CardTitle className="text-xl font-bold">{doc.fileName}</CardTitle>
                  <CardDescription className="mt-1">
                    Extracted topics and values
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="rounded-lg border border-border overflow-hidden">
                <Table>
                  <TableHeader className="bg-muted/50">
                    <TableRow>
                      <TableHead className="w-1/3 font-semibold">Topic</TableHead>
                      <TableHead className="font-semibold">Value</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {Object.entries(doc.results || {}).map(([topic, value], index) => (
                      <TableRow key={index} className="hover:bg-muted/30 transition-smooth">
                        <TableCell className="font-medium">
                          <Badge variant="outline" className="bg-primary/5 border-primary/20 text-primary">
                            {topic}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-foreground">
                          {value}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        ))}

        {/* Summary Stats */}
        <Card className="shadow-lg border-border/50">
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 rounded-lg bg-primary/5 border border-primary/10">
                <p className="text-xs text-muted-foreground mb-1">Total Documents Processed</p>
                <p className="text-2xl font-bold text-primary">{data.length}</p>
              </div>
              <div className="p-4 rounded-lg bg-accent/5 border border-accent/10">
                <p className="text-xs text-muted-foreground mb-1">Total Topics Extracted</p>
                <p className="text-2xl font-bold text-accent">
                  {data.reduce((sum, doc) => sum + Object.keys(doc.results || {}).length, 0)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
